import { useCallback, useEffect, useState } from "react";
import { PanelBadgeStarIcon, PanelBulletCheckIcon } from "@/components/icons/icons";
import { PanelStepProgress } from "@/components/PanelStepProgress";

/** Matches FeaturedProgramCarousel's cadence so the two feel like one product. */
const CROSS_SELL_ADVANCE_MS = 5000;

export interface MarketingPanelStat {
  value: string;
  label: string;
}

export interface MarketingPanelTestimonial {
  quote: string;
  name: string;
  role: string;
  /** Single-letter avatar initial */
  initial: string;
}

export interface MarketingPanelContent {
  badge?: string;
  heading: string;
  description: string;
  /** Illustration image shown in the panel card (imported PNG/SVG asset) */
  image?: string;
  bullets: string[];
  /** When provided, shows mini step progress indicator at top of panel */
  stepInfo?: {
    currentStep: number;
    totalSteps: number;
    stepLabel: string;
  };
  /** Optional stats strip (e.g. 300+ Experts / 2 hrs Per month / 4.8★ Avg. rating) */
  stats?: MarketingPanelStat[];
  /** Optional testimonial card shown below the stats */
  testimonial?: MarketingPanelTestimonial;
  /**
   * Optional secondary cards pitching the audiences the panel itself doesn't
   * cover, each with its own CTA. More than one renders as a carousel with
   * pagination dots — the sign-in page alternates an Achiever and an Expert
   * pitch below the shared platform blurb.
   */
  crossSell?: MarketingPanelCrossSell[];
}

export interface MarketingPanelCrossSell {
  /** Stable key for React lists */
  id: string;
  /** Small uppercase eyebrow, e.g. "For Career Builders" */
  badge: string;
  /** Bold one-liner above the bullets */
  heading: string;
  bullets: string[];
  ctaLabel: string;
  onCtaClick: () => void;
}

interface MarketingPanelProps {
  content: MarketingPanelContent;
}

export const MarketingPanel = ({ content }: MarketingPanelProps) => {
  const { badge, heading, description, image, bullets, stepInfo, stats, testimonial, crossSell } =
    content;

  // overflow-y-auto rather than overflow-hidden: the panel is taller than a
  // short viewport once it carries bullets plus a cross-sell card, and hidden
  // made the bottom of it unreachable instead of scrollable. The decorative
  // orbs still need overflow-x clipped.
  return (
    <div className="relative w-[384px] flex-shrink-0 h-full max-h-screen overflow-x-hidden overflow-y-auto bg-signup-panel">
      {/* Decorative orbs */}
      <div
        className="absolute rounded-full pointer-events-none w-96 h-96 opacity-20 bg-marketing-panel-orb-top"
        style={{ top: -96, right: -40 }}
      />
      <div
        className="absolute rounded-full pointer-events-none w-72 h-72 opacity-[0.13] bg-marketing-panel-orb-bottom"
        style={{ bottom: 20, left: -64 }}
      />

      <div className="relative flex flex-col gap-5 px-10 pt-8 pb-7 h-full">
        {/* Badge */}
        {badge && (
          <div className="inline-flex items-center gap-2 px-3 py-[5px] rounded-full border border-white/20 bg-white/15 self-start">
            <PanelBadgeStarIcon className="text-white/90" />
            <span className="text-[10px] font-semibold text-white/90 tracking-[1.1px] uppercase">
              {badge}
            </span>
          </div>
        )}

        {/* Mini step progress — shown for steps 2+ */}
        {stepInfo && (
          <PanelStepProgress
            currentStep={stepInfo.currentStep}
            totalSteps={stepInfo.totalSteps}
            stepLabel={stepInfo.stepLabel}
          />
        )}

        {/* Heading + description */}
        <div className="flex flex-col gap-2">
          <h2 className="text-[27px] font-bold text-white leading-[1.3] tracking-[0.3px]">
            {heading}
          </h2>
          <p className="text-[14px] text-white/70 leading-[1.55] tracking-[-0.1px]">
            {description}
          </p>
        </div>

        {/* Step illustration — grows to fill remaining space */}
        {image && (
          <div className="border border-white/15 bg-white/10 rounded-lg p-3 overflow-hidden mb-4">
            <img src={image} alt="" className="w-full object-contain rounded-lg" />
          </div>
        )}

        {/* Bullet points */}
        <div className="flex flex-col gap-5">
          {bullets.map((point, i) => (
            <div key={i} className="flex items-start items-center gap-3">
              <div className="flex-shrink-0 w-[20px] h-[20px] rounded-full bg-white/20 flex items-center justify-center mt-[1px]">
                <PanelBulletCheckIcon className="text-white/90" />
              </div>
              <p className="text-14 text-white/85 leading-[1.45]">{point}</p>
            </div>
          ))}
        </div>

        {/* Stats strip */}
        {stats && stats.length > 0 && (
          <div className="flex items-stretch rounded-2xl border border-white/15 bg-white/10 overflow-hidden">
            {stats.map((stat, i) => (
              <div
                key={i}
                className={`flex-1 flex flex-col items-center justify-center gap-1 py-5 px-4 ${
                  i < stats.length - 1 ? "border-r border-white/15" : ""
                }`}
              >
                <p className="text-16 font-bold text-white leading-none">{stat.value}</p>
                <p
                  className="text-[9px] font-medium uppercase tracking-[0.37px] text-white/55"
                  spellCheck={false}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Testimonial */}
        {testimonial && (
          <div className="rounded-2xl border border-white/15 bg-white/10 p-[21px]">
            <p className="font-serif text-4xl leading-none text-white/30">&ldquo;</p>
            <p className="text-14 text-white/90 leading-[1.6] mt-2">{testimonial.quote}</p>
            <div className="flex items-center gap-3 pt-4">
              <div className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                <span className="text-12 font-bold text-white">{testimonial.initial}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-12 font-semibold text-white">{testimonial.name}</span>
                <span className="text-[10px] text-white/55 tracking-[0.12px]">{testimonial.role}</span>
              </div>
            </div>
          </div>
        )}

        {/* Cross-sell — pitches the other audiences without leaving the page */}
        {crossSell && crossSell.length > 0 && <CrossSellCarousel slides={crossSell} />}
      </div>
    </div>
  );
};

/** Auto-advancing cross-sell carousel. Renders dots only when there is more
 *  than one slide, so a single card looks exactly like a plain card. */
const CrossSellCarousel = ({ slides }: { slides: MarketingPanelCrossSell[] }) => {
  const count = slides.length;
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const goTo = useCallback((next: number) => setIndex(((next % count) + count) % count), [count]);

  // Auto-advance, paused on hover/focus so the slide doesn't move out from
  // under someone reading it or tabbing to its CTA. Matches the cadence of
  // FeaturedProgramCarousel so the two don't feel like different products.
  useEffect(() => {
    if (count < 2 || isPaused) return;
    const timer = setTimeout(() => goTo(index + 1), CROSS_SELL_ADVANCE_MS);
    return () => clearTimeout(timer);
  }, [index, isPaused, count, goTo]);

  return (
    <div
      className="flex flex-col gap-2.5 items-center"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      <div className="w-full overflow-hidden rounded-[10px]">
        <div
          className="flex transition-transform duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <div
              key={slide.id}
              /* opacity-70 + rounded-[10px] per Figma: the card is a dark
                 translucent wash that lets the purple panel show through, so
                 the exact alpha is what makes it read as glass rather than a
                 flat slab. The radius is repeated here (the carousel's
                 overflow wrapper also has it) so a single slide stays
                 rounded on its own. */
              className="w-full shrink-0 rounded-[10px] bg-signup-panel-crosssell opacity-70 px-5 pt-5 pb-3"
              // Keep off-screen slides out of the tab order and the a11y tree
              // — otherwise Tab lands on a CTA nobody can see.
              aria-hidden={i !== index}
              {...(i !== index ? { inert: "" } : {})}
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-[10px] bg-white/15 flex items-center justify-center flex-shrink-0">
                  <PanelBadgeStarIcon className="text-white/90" />
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-[1.12px] text-white/60">
                  {slide.badge}
                </span>
              </div>

              <p className="text-16 font-bold text-white leading-[1.375] tracking-[-0.31px] pt-3">
                {slide.heading}
              </p>

              <div className="flex flex-col gap-2 py-4">
                {slide.bullets.map((point, b) => (
                  <div key={b} className="flex items-start gap-2">
                    <div className="w-4 h-4 mt-px rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                      <PanelBulletCheckIcon className="text-white/90" />
                    </div>
                    <p className="text-12 text-white/75 leading-4">{point}</p>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={slide.onCtaClick}
                className="w-full h-[42px] rounded-[14px] border border-white/25 bg-signup-panel-crosssell-cta text-14 font-semibold text-white tracking-[-0.15px] transition-opacity hover:opacity-90"
              >
                {slide.ctaLabel}
              </button>
            </div>
          ))}
        </div>
      </div>

      {count > 1 && (
        <div className="flex items-center gap-1.5">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`Show ${slide.badge}`}
              aria-current={i === index}
              onClick={() => goTo(i)}
              className={`h-2 rounded-full transition-all ${
                i === index ? "w-2 bg-white" : "w-2 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
